import { Ionicons } from '@expo/vector-icons';
import React from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';

import { Text } from '@/components/ui';
import { ProgressBar } from '@/components/ui/progress-bar';
import {
	buildQbBundleProgressView,
	formatDurationShort,
	type QbBundleProgressOptions,
	type QbBundleStepStatus,
	type QbBundleStepView,
} from '@/utils/qb-bundle-progress';

export type { QbBundleProgressOptions };

interface Props extends QbBundleProgressOptions {
	/** Shown above the graph when the workflow failed. */
	errorMessage?: string | null;
}

function stepIcon(status: QbBundleStepStatus): React.ReactNode {
	switch (status) {
		case 'complete':
			return <Ionicons name="checkmark-circle" size={22} color="#2e7d32" />;
		case 'active':
			return <ActivityIndicator size="small" color="#1976d2" />;
		case 'error':
			return <Ionicons name="close-circle" size={22} color="#c62828" />;
		case 'skipped':
			return <Ionicons name="remove-circle-outline" size={22} color="#b0bec5" />;
		default:
			return <View style={styles.stepDotPending} />;
	}
}

/** One-line gloss of the workflow's current-step beacon output. */
function backendStepGloss(detail: { input?: unknown; output?: unknown } | null | undefined): string {
	const out = detail?.output as Record<string, unknown> | undefined;
	if (!out) return '';
	const parts: string[] = [];
	if (typeof out.remaining === 'number') parts.push(`${out.remaining.toLocaleString()} left`);
	if (typeof out.posted === 'number') parts.push(`${out.posted.toLocaleString()} posted`);
	if (typeof out.matched === 'number') parts.push(`${out.matched.toLocaleString()} matched`);
	return parts.length ? ` · ${parts.join(' · ')}` : '';
}

function stepStatusLabel(step: QbBundleStepView): string {
	if (step.status === 'complete') return 'Done';
	if (step.status === 'error') return 'Failed';
	if (step.status === 'active') {
		if (step.percent != null) return `${step.percent}%`;
		return 'In progress…';
	}
	return 'Waiting';
}

const StepRow: React.FC<{ step: QbBundleStepView; isLast: boolean }> = ({ step, isLast }) => {
	const isActive = step.status === 'active';
	const isError = step.status === 'error';
	const connectorDone = step.status === 'complete';

	return (
		<View style={styles.stepRow}>
			<View style={styles.stepRail}>
				<View style={[
					styles.stepIconWrap,
					isActive && styles.stepIconWrapActive,
					isError && styles.stepIconWrapError,
					step.status === 'complete' && styles.stepIconWrapDone,
				]}>
					{stepIcon(step.status)}
				</View>
				{!isLast && (
					<View style={[styles.connector, connectorDone && styles.connectorDone]} />
				)}
			</View>
			<View style={styles.stepBody}>
				<View style={styles.stepHeader}>
					<Text style={[styles.stepTitle, isActive && styles.stepTitleActive, isError && styles.stepTitleError]}>
						{step.title}
					</Text>
					<Text style={[styles.stepBadge, isError && styles.stepBadgeError, step.status === 'complete' && styles.stepBadgeDone]}>
						{stepStatusLabel(step)}
					</Text>
				</View>
				<Text style={styles.stepDesc}>{step.description}</Text>
				{(isActive || step.status === 'complete') && step.percent != null && step.status !== 'complete' && (
					<View style={styles.stepBarWrap}>
						<ProgressBar progress={step.percent / 100} style={styles.stepBar} />
					</View>
				)}
				{step.etaMs != null && step.etaMs > 0 && (isActive || step.status === 'pending') && (
					<Text style={styles.stepEta}>Est. {formatDurationShort(step.etaMs)} remaining</Text>
				)}
			</View>
		</View>
	);
};

export const QbBundleProgressView: React.FC<Props> = (props) => {
	const view = buildQbBundleProgressView(props);
	const { overallPercent, overallEtaMs, elapsedLabel, steps, activeStepId } = view;
	const failed = props.status?.workflowStatus === 'errored' || props.status?.workflowStatus === 'terminated';

	return (
		<View style={styles.wrap}>
			{props.errorMessage && (
				<View style={styles.errorBanner}>
					<Ionicons name="alert-circle" size={18} color="#b71c1c" />
					<Text style={styles.errorText}>{props.errorMessage}</Text>
				</View>
			)}

			<View style={styles.overallCard}>
				<View style={styles.overallHeader}>
					<Text style={styles.overallTitle}>
						{failed ? 'Rebuild failed' : overallPercent >= 100 ? 'Rebuild complete' : 'Overall progress'}
					</Text>
					<Text style={styles.overallPercent}>{overallPercent}%</Text>
				</View>
				<ProgressBar
					progress={overallPercent / 100}
					color={failed ? '#e57373' : overallPercent >= 100 ? '#43a047' : '#1976d2'}
					style={styles.overallBar}
				/>
				<View style={styles.overallMeta}>
					<Text style={styles.metaText}>{elapsedLabel}</Text>
					{!failed && overallPercent < 100 && overallEtaMs != null && overallEtaMs > 0 && (
						<Text style={styles.metaText}>Est. {formatDurationShort(overallEtaMs)} left</Text>
					)}
					{activeStepId && !failed && (
						<Text style={styles.metaActive}>Step: {steps.find((s) => s.id === activeStepId)?.title}</Text>
					)}
				</View>
				{/* EXACT backend step beacon (issue #248) — the real workflow step name
				    + what that step last reported (e.g. "spine chunk 12 · 3,000 left"). */}
				{props.status?.currentStep && !failed && overallPercent < 100 && (
					<Text style={styles.backendStep} selectable>
						{props.status.currentStep}{backendStepGloss(props.status.currentStepDetail)}
					</Text>
				)}
			</View>

			<View style={styles.graphCard}>
				<Text style={styles.graphTitle}>Workflow</Text>
				{steps.map((step, idx) => (
					<StepRow key={step.id} step={step} isLast={idx === steps.length - 1} />
				))}
			</View>
		</View>
	);
};

const styles = StyleSheet.create({
	wrap: { alignSelf: 'stretch', gap: 14 },
	errorBanner: {
		flexDirection: 'row',
		alignItems: 'flex-start',
		gap: 8,
		backgroundColor: '#ffebee',
		borderRadius: 8,
		padding: 12,
		borderWidth: 1,
		borderColor: '#ffcdd2',
	},
	errorText: { flex: 1, fontSize: 12, color: '#b71c1c', lineHeight: 17 },
	overallCard: {
		backgroundColor: '#e3f2fd',
		borderRadius: 10,
		padding: 14,
		gap: 8,
		borderWidth: 1,
		borderColor: '#bbdefb',
	},
	overallHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
	overallTitle: { fontSize: 14, fontWeight: '700', color: '#1565c0' },
	overallPercent: { fontSize: 20, fontWeight: '800', color: '#0d47a1' },
	overallBar: { height: 10, borderRadius: 5 },
	overallMeta: { flexDirection: 'row', flexWrap: 'wrap', gap: 10 },
	metaText: { fontSize: 11, color: '#546e7a', fontWeight: '600' },
	metaActive: { fontSize: 11, color: '#1976d2', fontWeight: '700' },
	backendStep: { fontSize: 11, color: '#607d8b', fontFamily: 'ui-monospace, Menlo, monospace', marginTop: 2 },
	graphCard: {
		backgroundColor: '#fafafa',
		borderRadius: 10,
		padding: 14,
		borderWidth: 1,
		borderColor: '#eceff1',
	},
	graphTitle: { fontSize: 12, fontWeight: '700', color: '#78909c', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8 },
	stepRow: { flexDirection: 'row', gap: 12 },
	stepRail: { width: 28, alignItems: 'center' },
	stepIconWrap: {
		width: 28,
		height: 28,
		borderRadius: 14,
		backgroundColor: '#fff',
		borderWidth: 2,
		borderColor: '#cfd8dc',
		alignItems: 'center',
		justifyContent: 'center',
	},
	stepIconWrapActive: { borderColor: '#1976d2', backgroundColor: '#e3f2fd' },
	stepIconWrapDone: { borderColor: '#43a047', backgroundColor: '#e8f5e9' },
	stepIconWrapError: { borderColor: '#c62828', backgroundColor: '#ffebee' },
	stepDotPending: {
		width: 10,
		height: 10,
		borderRadius: 5,
		backgroundColor: '#cfd8dc',
	},
	connector: {
		flex: 1,
		width: 2,
		minHeight: 24,
		backgroundColor: '#e0e0e0',
		marginVertical: 2,
	},
	connectorDone: { backgroundColor: '#81c784' },
	stepBody: { flex: 1, paddingBottom: 16, minWidth: 0 },
	stepHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 },
	stepTitle: { flex: 1, fontSize: 13, fontWeight: '700', color: '#455a64' },
	stepTitleActive: { color: '#1565c0' },
	stepTitleError: { color: '#c62828' },
	stepBadge: {
		fontSize: 10,
		fontWeight: '700',
		color: '#90a4ae',
		backgroundColor: '#eceff1',
		paddingHorizontal: 8,
		paddingVertical: 3,
		borderRadius: 10,
		overflow: 'hidden',
	},
	stepBadgeDone: { color: '#2e7d32', backgroundColor: '#e8f5e9' },
	stepBadgeError: { color: '#c62828', backgroundColor: '#ffcdd2' },
	stepDesc: { fontSize: 11, color: '#78909c', marginTop: 3, lineHeight: 16 },
	stepBarWrap: { marginTop: 8 },
	stepBar: { height: 6, borderRadius: 3 },
	stepEta: { fontSize: 10, color: '#1976d2', marginTop: 4, fontWeight: '600' },
});
